/**
 * QuickBooks Online integration card.
 *
 * Mirrors SlackCard/TeamsCard but is NOT plan-gated (the Settings
 * page already restricts Integrations to SUPER_ADMIN; add a
 * usePlanFeatures gate here later if QBO becomes a paid tier). States:
 *   1. Loading      → skeleton
 *   2. Connected    → realm + environment + last-sync status, Sync now + Disconnect
 *   3. Not connected → explanation + "Connect QuickBooks"
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, RefreshCw, Receipt, AlertTriangle } from "lucide-react";

import {
  disconnectQuickBooks,
  getQuickBooksInstallUrl,
  getQuickBooksStatus,
  syncQuickBooksNow,
  type QuickBooksStatusResponse,
} from "@/api/integrationsQuickbooks";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useToast } from "@/hooks/useToast";
import { formatDate } from "@/lib/utils";

export function QuickBooksCard() {
  const { addToast } = useToast();
  const [params, setParams] = useSearchParams();

  const [status, setStatus] = useState<QuickBooksStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await getQuickBooksStatus());
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Surface the OAuth-callback redirect-back state.
  useEffect(() => {
    const ok = params.get("quickbooks_connected");
    const err = params.get("quickbooks_error");
    if (ok) {
      addToast("success", "QuickBooks connected!");
      params.delete("quickbooks_connected");
      setParams(params, { replace: true });
      void refresh();
    } else if (err) {
      addToast("error", `QuickBooks connection failed: ${err}`);
      params.delete("quickbooks_error");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setInstalling(true);
    try {
      const res = await getQuickBooksInstallUrl();
      window.location.href = res.authorize_url;
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not start the QuickBooks connection.";
      addToast("error", detail);
      setInstalling(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const res = await disconnectQuickBooks();
      setStatus(res);
      addToast("success", "QuickBooks disconnected.");
    } catch {
      addToast("error", "Could not disconnect QuickBooks.");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await syncQuickBooksNow();
      if (res.ok) {
        addToast("success", "Sync started — refreshing status.");
        await refresh();
      } else {
        addToast("error", res.detail || "Sync failed.");
      }
    } catch {
      addToast("error", "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-accent/10 p-2">
            <Receipt className="h-5 w-5 text-accent" aria-hidden />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-heading text-text-primary">
              QuickBooks Online
            </h3>
            <p className="text-[11px] uppercase tracking-wider text-text-muted">
              Accounting
            </p>
          </div>
        </div>
        {!loading && status?.connected && (
          <Badge variant="success">Connected</Badge>
        )}
        {!loading && status && !status.connected && status.ever_installed && (
          <Badge variant="warning">Disconnected</Badge>
        )}
        {!loading && status && !status.connected && !status.ever_installed && (
          <Badge variant="default">Not connected</Badge>
        )}
      </div>

      {loading && <div className="h-12 rounded bg-border/30 animate-pulse" />}

      {/* Connected */}
      {!loading && status?.connected && (
        <>
          <p className="text-sm mb-2 text-text-primary">
            <Check
              className="inline h-4 w-4 -mt-0.5 mr-1 text-success"
              aria-hidden
            />
            Connected to company <strong>{status.realm_id}</strong>
            {status.environment ? (
              <span className="text-text-muted"> ({status.environment})</span>
            ) : null}
          </p>

          <div className="rounded-lg border border-dashed border-border bg-background p-3 text-xs text-text-muted space-y-1.5 mb-4">
            <p className="text-text-primary font-medium">Auto-sync every 15 min</p>
            <ul className="space-y-0.5 ml-4">
              <li>Invoices → QBO invoices</li>
              <li>Approved expenses → QBO purchases</li>
              <li>Approved timesheets → QBO time activities</li>
              <li>Employees → QBO employee records</li>
              <li>Payroll/employee data ← reconciled against Lenavio</li>
            </ul>
            <p className="pt-1">
              Last sync:{" "}
              {status.last_sync_at ? (
                <>
                  {formatDate(status.last_sync_at, "MMM d, h:mm a")}{" "}
                  {status.last_sync_status === "ok" ? (
                    <span className="text-success">· ok</span>
                  ) : status.last_sync_status === "error" ? (
                    <span className="text-danger">· error</span>
                  ) : null}
                </>
              ) : (
                "not yet"
              )}
            </p>
            {status.last_sync_status === "error" && status.last_sync_error && (
              <p className="flex items-start gap-1 text-danger">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                {status.last_sync_error}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSyncNow} isLoading={syncing}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Sync now
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDisconnect}
              isLoading={disconnecting}
            >
              Disconnect
            </Button>
          </div>
        </>
      )}

      {/* Not connected */}
      {!loading && status && !status.connected && (
        <>
          <p className="text-sm text-text-muted mb-4">
            Sync invoices, approved expenses, and timesheets into
            QuickBooks Online, keep employee records aligned, and
            reconcile payroll — automatically, every 15 minutes.
          </p>
          <Button onClick={handleConnect} isLoading={installing}>
            <Receipt className="h-4 w-4" />
            Connect QuickBooks
          </Button>
        </>
      )}
    </Card>
  );
}
